import { ApiClient } from '@ifixit/carpenter-utils';
import { _js } from '@ifixit/utils/localize';
import { Alert, AlertDescription, AlertTitle } from '@ifixit/ui/components/alert';
import { Button } from '@ifixit/ui/components/button';
import { FaIcon } from '@ifixit/ui/components/icon';
import { cn } from '@ifixit/ui/lib/utils';
import {
   AlertDialog,
   AlertDialogAction,
   AlertDialogCancel,
   AlertDialogContent,
   AlertDialogDescription,
   AlertDialogFooter,
   AlertDialogHeader,
   AlertDialogTitle,
} from '@ifixit/ui/components/alert-dialog';
import {
   DropdownMenu,
   DropdownMenuContent,
   DropdownMenuItem,
   DropdownMenuTrigger,
} from '@ifixit/ui/components/dropdown-menu';
import { Skeleton } from '@ifixit/ui/components/skeleton';
import { Tabs, TabsContent, TabsList } from '@ifixit/ui/components/tabs';
import { toast, Toaster } from '@ifixit/ui/components/toast';
import { useEffect, useMemo, useRef, useState } from 'react';
import { callAjaxIO } from 'Shared/ajax_io';
import { BAR_CLASSES, WriteStatusAnnouncer } from 'Shared/editor/action_bar';
import { apiErrorMessage } from 'Shared/editor/api_error_message';
import { PrimaryButton } from 'Shared/editor/buttons';
import { EditorFooter } from 'Shared/editor/editor_footer';
import { EditorTab, useArrowTabNav, useHashTab } from 'Shared/editor/hash_tabs';
import { EDGE_INSET_CLASSES, SectionCard } from 'Shared/editor/section_card';
import { useSaveShortcut } from 'Shared/editor/use_save_shortcut';
import { useUnsavedChangesGuard } from 'Shared/editor/use_unsaved_changes_guard';
import { DetailsTab } from './DetailsTab';
import {
   buildGuidePatch,
   conflictingFields,
   type GuideEditFields,
   initialGuideFields,
} from './guide_patch';
import { applyIntroVideo } from './intro_video_token';
import { IntroTab } from './IntroTab';
import { KeyboardShortcutsDialog } from './KeyboardShortcutsDialog';
import { MetadataTab } from './MetadataTab';
import { useWikitextEditor } from 'Shared/editor/use_wikitext_editor';
import { StepsTab } from './StepsTab';
import type { ApiGuide, GuideEditV2Props } from './types';
import {
   guideTextToJson,
   topicExists,
   useDeleteGuide,
   useGuidePrerequisites,
   useGuideQuery,
   useHostedVideo,
   useSaveGuide,
} from './use_guide_edit_api';
import { SentryError } from '@ifixit/sentry';

export function GuideEditPage(props: GuideEditV2Props) {
   const query = useGuideQuery(props.guideid, props.langid);
   useMediaManagerDeepLink();

   return (
      // Below md the cards go full-bleed (SectionCard drops its corners and
      // side borders), so the root carries no horizontal padding there;
      // rows that aren't cards pad themselves back to 16px.
      <div className="mx-auto w-full max-w-5xl pb-8 md:px-8">
         <Toaster />

         {/* A failed background refetch flips isError while data is still
             cached; keep the editor mounted (unmounting would discard every
             unsaved edit) and only show the error page when nothing ever
             loaded. */}
         {query.data ? (
            <LoadedEditor guide={query.data} {...props} />
         ) : query.isError ? (
            <Alert variant="critical" className={EDGE_INSET_CLASSES}>
               <AlertTitle>{_js('The guide failed to load.')}</AlertTitle>
               <AlertDescription>{errorMessage(query.error)}</AlertDescription>
            </Alert>
         ) : (
            <PageSkeleton initialTab={props.initialTab} />
         )}
      </div>
   );
}

function LoadedEditor({ guide, ...props }: GuideEditV2Props & { guide: ApiGuide }) {
   const boot = {
      metadata: props.metadata,
      prerequisites: props.prerequisites,
      guideType: props.guideType?.current,
   };
   const [baseline, setBaseline] = useState(() => initialGuideFields(guide, boot));
   const [fields, setFields] = useState(baseline);
   const [prerequisiteDetails, setPrerequisiteDetails] = useState(props.prerequisites);
   const [stepsDirty, setStepsDirty] = useState(false);
   // The keyboard-shortcuts reference, opened from the footer link (any tab)
   // and from Shift+/ on the Steps tab.
   const [shortcutsOpen, setShortcutsOpen] = useState(false);
   // The Metadata tab is admin + moderator only. Flags moved to the Details
   // tab, so what's left here (patrol threshold, tags, private-guide
   // allowlists) is a moderation surface; hide the whole tab without the
   // privilege even if a section would otherwise have data.
   const hasMetadataTab =
      props.canEditMetadata &&
      (props.metadata.canChangePrivacy ||
         props.metadata.patrol !== null ||
         props.metadata.tags !== null);
   const tabOrder = hasMetadataTab
      ? ['intro', 'details', 'steps', 'metadata']
      : ['intro', 'details', 'steps'];
   const [activeTab, setActiveTab] = useHashTab(tabOrder, props.initialTab);

   useArrowTabNav(tabOrder, activeTab, setActiveTab);

   const [metaError, setMetaError] = useState<string | null>(null);
   // Pending across the whole performSave chain. save.isPending only covers
   // the PATCH: it's false for tags/threshold-only saves and flips false
   // before their AjaxIO calls finish, which would let a second click race
   // the first save's metadata phase.
   const [isSaving, setIsSaving] = useState(false);
   // Guards the async gap before performSave() flips isSaving; see handleSave.
   const savePendingRef = useRef(false);
   const [isDeleting, setIsDeleting] = useState(false);
   // True while the Intro tab's main image is producing a cropped copy in the
   // background; a save then would persist the pre-crop imageid and orphan the
   // new one, so it blocks the save until the crop's imageid lands.
   const [isImageProcessing, setIsImageProcessing] = useState(false);
   // React Query's pending state updates on render. Keep a synchronous guard
   // too so Save cannot start between confirming Delete and that render.
   const deletePendingRef = useRef(false);
   // A nonexistent category name the pending save would create; non-null
   // renders the create-category confirmation dialog.
   const [confirmCategory, setConfirmCategory] = useState<string | null>(null);
   const save = useSaveGuide(props.guideid);
   const deleteGuide = useDeleteGuide(props.guideid);
   // Flipped synchronously right before the post-delete redirect so the
   // beforeunload guard below doesn't prompt "Leave site?" on the way out —
   // the user just confirmed the delete, and their unsaved edits are gone
   // with the guide anyway.
   const deletingRef = useRef(false);
   // Introduction and conclusion live in Tiptap editors, not plain field
   // state: their contents serialize to wikitext at save time.
   const introEditor = useWikitextEditor(props.introduction, 'guideIntroField', guideTextToJson);
   const conclusionEditor = useWikitextEditor(
      props.conclusion,
      'guideConclusionField',
      guideTextToJson
   );

   // Step adds/deletes/reorders refetch the guide with a fresh revisionid.
   // Reconcile during render (the sanctioned derived-state adjustment):
   // untouched fields adopt the fresh server values and the baseline moves
   // with them, so a later guide save can't pair the fresh revisionid with
   // diffs computed against a stale snapshot — that combination silently
   // clobbers concurrent edits the 409 guard exists to catch.
   const [seededRevisionid, setSeededRevisionid] = useState(guide.revisionid);
   // A concurrent edit was detected while advancing the base (see below);
   // sticky until the page reloads, since advancing already conceded the 409.
   const [guideConflict, setGuideConflict] = useState(false);
   // The server intro/conclusion wikitext at the last reconcile, tracked as
   // state (a ref can't be written during render) and advanced in the same
   // block as the revision. Compared server-to-server on the next refetch:
   // their editors serialize a stripped form, so a direct editor comparison
   // would false-positive on the managed video token.
   const [seededIntroRaw, setSeededIntroRaw] = useState(guide.introduction_raw ?? '');
   const [seededConclusionRaw, setSeededConclusionRaw] = useState(guide.conclusion_raw ?? '');
   const revisionChanged = guide.revisionid !== seededRevisionid;
   const prerequisiteRefresh = useGuidePrerequisites(
      props.guideid,
      props.langid,
      guide.revisionid,
      revisionChanged
   );
   const revisionRefreshPending = revisionChanged && prerequisiteRefresh.data === undefined;
   if (revisionChanged && prerequisiteRefresh.data !== undefined) {
      setSeededRevisionid(guide.revisionid);
      // Tags, threshold, and type don't ride either refreshed response; their
      // baseline is whatever was last loaded or saved. Prerequisites come from
      // the edit-only unfiltered read, never the visibility-filtered guide GET.
      const fresh = {
         ...initialGuideFields(guide, { prerequisites: prerequisiteRefresh.data }),
         tags: baseline.tags,
         patrolThreshold: baseline.patrolThreshold,
         type: baseline.type,
      };
      // The step op advanced the revision, so the guide save can't 409 on a
      // field another editor changed first; catch that overlap before the
      // base advances past it. Intro/conclusion aren't in `fields`; compare
      // their server raw across refetches. A clean editor still holds stale
      // text, so any server change that does not equal the current local wire
      // value must conflict rather than advancing the revision underneath it.
      const freshIntroRaw = guide.introduction_raw ?? '';
      const freshConclusionRaw = guide.conclusion_raw ?? '';
      const localIntroRaw = applyIntroVideo(introEditor.getContents(), fields.video);
      const introChanged = freshIntroRaw !== seededIntroRaw && freshIntroRaw !== localIntroRaw;
      const conclusionChanged =
         freshConclusionRaw !== seededConclusionRaw &&
         freshConclusionRaw !== conclusionEditor.getContents();
      if (
         conflictingFields(fields, baseline, fresh).length > 0 ||
         introChanged ||
         conclusionChanged
      ) {
         setGuideConflict(true);
      }
      setSeededIntroRaw(freshIntroRaw);
      setSeededConclusionRaw(freshConclusionRaw);
      setPrerequisiteDetails(prerequisiteRefresh.data);
      setFields(current => mergeUntouchedFields(current, baseline, fresh));
      setBaseline(fresh);
   }

   const isVideoGuide =
      props.video !== null || (fields.video?.type === 'youtube' && fields.video.mode === 'guide');
   const nativeVideoid = fields.video?.type === 'native' ? fields.video.videoid : null;
   const hostedVideo = useHostedVideo(nativeVideoid);
   const stepVideo =
      !isVideoGuide || fields.video === null
         ? null
         : fields.video.type === 'youtube'
           ? fields.video.mode === 'guide'
              ? { type: 'youtube' as const, videoId: fields.video.videoId, videoUrls: null }
              : null
           : props.video?.type === 'ifixit' && props.video.videoId === String(fields.video.videoid)
             ? props.video
             : (hostedVideo.data?.stepVideo ?? null);

   // Memoized: this JSON-diffs every field (including the full intro and
   // conclusion wikitext) and otherwise re-runs per keystroke.
   const patch = useMemo(
      () => buildGuidePatch(fields, baseline, { releasesEnabled: props.releasesEnabled }),
      [fields, baseline, props.releasesEnabled]
   );
   // Tags and the patrol threshold save through their own AjaxIO channels,
   // so they dirty the page without appearing in the PATCH body.
   const tagsChanged =
      props.metadata.tags !== null && JSON.stringify(fields.tags) !== JSON.stringify(baseline.tags);
   const thresholdChanged =
      props.metadata.patrol !== null && fields.patrolThreshold !== baseline.patrolThreshold;
   const introDirty = introEditor.isDirty();
   const conclusionDirty = conclusionEditor.isDirty();
   // The Video field saves by composing its token back into the
   // introduction wikitext, so it dirties the intro rather than the PATCH.
   const videoChanged = JSON.stringify(fields.video) !== JSON.stringify(baseline.video);
   const guideDirty =
      Object.keys(patch).length > 0 ||
      tagsChanged ||
      thresholdChanged ||
      videoChanged ||
      introDirty ||
      conclusionDirty;
   // Visibility is guide state, not a form field: the header's Publish
   // control PATCHes { public } alone through its own mutation instance, so
   // unsaved form edits stay local and untouched. The reseed on success runs
   // the revision reconcile above, advancing the baseline (isPublic included)
   // so a later form save can't 409 against the visibility bump.
   const publish = useSaveGuide(props.guideid);
   const saveDisabled =
      isSaving ||
      isDeleting ||
      isImageProcessing ||
      publish.isPending ||
      !guideDirty ||
      guideConflict ||
      revisionRefreshPending;
   useUnsavedChangesGuard(() => !deletingRef.current && (guideDirty || stepsDirty));

   // Which tab each unsaved change lives on, so its trigger can carry a dot.
   // Intro claims its own patch keys; Details is the catch-all for every other
   // key (including flags), so a field added later can't go silently un-dotted.
   // The Metadata tab's tags and threshold never ride the PATCH.
   const patchKeys = Object.keys(patch);
   const introKeys = new Set(['title', 'category', 'subject', 'type', 'image']);
   const tabDirty = {
      intro: introDirty || videoChanged || patchKeys.some(key => introKeys.has(key)),
      details: conclusionDirty || patchKeys.some(key => !introKeys.has(key)),
      steps: stepsDirty,
      metadata: tagsChanged || thresholdChanged,
   };

   const isPublic = Boolean(guide.public);
   const publishBusy = publish.isPending || isSaving || isDeleting;
   const handleSetPublic = async (nextPublic: boolean) => {
      try {
         await publish.mutateAsync({
            revisionid: guide.revisionid,
            patch: { public: nextPublic },
         });
         toast.success(
            nextPublic ? _js('Your guide is now public.') : _js('Your guide is now private.')
         );
      } catch (error) {
         toast.error(
            apiErrorMessage(error as Error, {
               conflict: _js(
                  'Someone else edited this guide. Reload the page to pick up their changes.'
               ),
            })
         );
      }
   };

   const handleSave = async () => {
      // isSaving is React state: it doesn't flip until performSave() runs,
      // so it can't gate the async topicExists() lookup below. A synchronous
      // ref does — without it a double-click starts two saves on the same
      // revision, the second surfacing a stale-edit error after the first
      // succeeds. Cleared when confirmation is required (the save hands off
      // to the dialog) or when the save finishes.
      if (
         savePendingRef.current ||
         isSaving ||
         deletePendingRef.current ||
         isDeleting ||
         isImageProcessing ||
         guideConflict ||
         revisionRefreshPending
      ) {
         return;
      }
      savePendingRef.current = true;
      try {
         setMetaError(null);
         // On auto-category-creation sites (including ifixit.com) the server
         // creates whatever category name arrives, so confirm before
         // re-parenting the guide onto a topic that doesn't exist.
         if ('category' in patch && !(await topicExists(fields.category))) {
            setConfirmCategory(fields.category);
            return;
         }
         await performSave();
      } finally {
         savePendingRef.current = false;
      }
   };

   // The Steps tab has no guide-level Save button; StepEditor binds the chord
   // to its own per-step save instead.
   useSaveShortcut(() => {
      if (activeTab !== 'steps' && !saveDisabled) {
         void handleSave();
      }
   });

   const performSave = async () => {
      // Distinguishes PATCH failures (surfaced via save.isError) from the
      // AjaxIO phases; save.isError inside the catch is a stale closure.
      let phase: 'patch' | 'meta' = 'patch';
      setIsSaving(true);
      // Clear a prior PATCH error: a metadata-only save (empty patchBody)
      // never runs the mutation, so its isError would otherwise leave the
      // "Your changes were not saved" banner up next to the success toast.
      save.reset();
      // `fields` here is the submission-time snapshot (this closure's
      // render). Edits made while the requests run merge back in at the end
      // instead of being clobbered by these submitted values.
      const submitted = { ...fields };
      // The editor holds the intro stripped of the managed video token
      // (GuideEditLib seeds it that way); the wire format carries the token,
      // so compose it back in. Rebaseline gets the editor's own (stripped)
      // contents — the composed text would compare as forever-dirty.
      const editorIntro = introDirty || videoChanged ? introEditor.getContents() : null;
      const submittedIntro =
         editorIntro === null ? null : applyIntroVideo(editorIntro, submitted.video);
      const submittedConclusion = conclusionDirty ? conclusionEditor.getContents() : null;
      try {
         const patchBody: Record<string, unknown> = { ...patch };
         if (submittedIntro !== null) {
            patchBody.introduction = submittedIntro;
         }
         if (submittedConclusion !== null) {
            patchBody.conclusion = submittedConclusion;
         }
         const saved =
            Object.keys(patchBody).length > 0
               ? await save.mutateAsync({ revisionid: guide.revisionid, patch: patchBody })
               : guide;
         if (editorIntro !== null) {
            introEditor.rebaseline(editorIntro);
         }
         if (submittedConclusion !== null) {
            conclusionEditor.rebaseline(submittedConclusion);
         }
         phase = 'meta';
         let nextTags = submitted.tags;
         if (tagsChanged) {
            const result = await callAjaxIO<{ tags?: string[]; error?: string }>(
               'saveTags',
               props.guideid,
               'guide',
               submitted.tags
            );
            if (result?.error) {
               throw new SentryError(result.error);
            }
            nextTags = result?.tags ?? submitted.tags;
            // Commit this phase immediately: a later threshold failure must
            // not make the retry submit the same tag edit again. Keep newer
            // tags the user entered while this request was in flight.
            setBaseline(current => ({ ...current, tags: nextTags }));
            setFields(current =>
               JSON.stringify(current.tags) === JSON.stringify(submitted.tags)
                  ? { ...current, tags: nextTags }
                  : current
            );
         }
         if (thresholdChanged && props.metadata.patrol) {
            // The display max stands in for the infinite sentinel.
            const threshold =
               submitted.patrolThreshold >= props.metadata.patrol.maxDisplayed
                  ? 'inf'
                  : submitted.patrolThreshold;
            await callAjaxIO('saveThreshold', 'guide', props.guideid, threshold);
         }
         const next = {
            ...initialGuideFields(saved),
            tags: nextTags,
            patrolThreshold: submitted.patrolThreshold,
            prerequisites: submitted.prerequisites,
            // Guide type is bootstrapped by PHP and absent from the API
            // response, so keep the submitted value in the fresh baseline.
            type: submitted.type,
         };
         setBaseline(next);
         // Fields touched since submission keep their newer values (and
         // stay dirty against the fresh baseline); the rest adopt the
         // server's canonical state.
         setFields(current => mergeUntouchedFields(current, submitted, next));
         toast.success(_js('Guide saved.'), {
            duration: 4000,
            action: {
               label: _js('View guide'),
               onClick: () => window.open(props.viewUrl, '_blank'),
            },
         });
      } catch (error) {
         if (phase === 'meta') {
            setMetaError(error instanceof Error ? error.message : String(error));
         }
      } finally {
         setIsSaving(false);
      }
   };

   const handleConfirmedCategorySave = async () => {
      setConfirmCategory(null);
      if (
         savePendingRef.current ||
         isSaving ||
         deletePendingRef.current ||
         isDeleting ||
         isImageProcessing ||
         guideConflict ||
         revisionRefreshPending
      ) {
         return;
      }
      savePendingRef.current = true;
      try {
         await performSave();
      } finally {
         savePendingRef.current = false;
      }
   };

   const handleDelete = async () => {
      if (deletePendingRef.current || isDeleting || savePendingRef.current || isSaving) {
         return;
      }
      deletePendingRef.current = true;
      setIsDeleting(true);
      try {
         await deleteGuide.mutateAsync({ revisionid: guide.revisionid });
         // Suppress the unsaved-changes guard, then leave for the device
         // page. The local pending state holds the "Deleting..." label through
         // the redirect instead of waiting for React Query to rerender.
         deletingRef.current = true;
         window.location.assign(props.deleteRedirectUrl);
      } catch (error) {
         toast.error(
            deleteErrorMessage(error instanceof Error ? error : new SentryError(String(error)))
         );
      } finally {
         deletePendingRef.current = false;
         setIsDeleting(false);
      }
   };

   return (
      <>
         <WriteStatusAnnouncer isSaving={isSaving} isDeleting={isDeleting} />
         {save.isError && (
            <Alert variant="critical" className={cn('mb-4', EDGE_INSET_CLASSES)}>
               <AlertTitle>{_js('Your changes were not saved.')}</AlertTitle>
               <AlertDescription>
                  {errorMessage(save.error)}
                  {isConflictError(save.error) && (
                     <Button
                        className={cn('button-md button-outline', 'mt-2')}
                        onClick={() => window.location.reload()}
                     >
                        {_js('Reload page')}
                     </Button>
                  )}
               </AlertDescription>
            </Alert>
         )}
         {metaError && (
            <Alert variant="critical" className={cn('mb-4', EDGE_INSET_CLASSES)}>
               <AlertTitle>{_js('Some changes were not saved.')}</AlertTitle>
               <AlertDescription>{metaError}</AlertDescription>
            </Alert>
         )}
         {guideConflict && (
            <Alert variant="critical" className={cn('mb-4', EDGE_INSET_CLASSES)}>
               <AlertTitle>
                  {_js('Someone else edited this guide while you were working.')}
               </AlertTitle>
               <AlertDescription>
                  <p>
                     {_js(
                        'Saving now would overwrite their changes. Reload to pick up the latest version, then redo your edits on top of it. (Any steps you saved are already stored.)'
                     )}
                  </p>
                  <Button
                     type="button"
                     className={cn('button-md button-outline', 'mt-2')}
                     onClick={() => window.location.reload()}
                  >
                     {_js('Reload the guide')}
                  </Button>
               </AlertDescription>
            </Alert>
         )}
         {revisionChanged && prerequisiteRefresh.isError && (
            <Alert variant="critical" className={cn('mb-4', EDGE_INSET_CLASSES)}>
               <AlertTitle>{_js('The latest guide changes could not be loaded.')}</AlertTitle>
               <AlertDescription>
                  {_js('Reload the guide before saving so no one else’s changes are overwritten.')}
               </AlertDescription>
            </Alert>
         )}

         <AlertDialog
            open={confirmCategory !== null}
            onOpenChange={open => {
               if (!open) {
                  setConfirmCategory(null);
               }
            }}
         >
            <AlertDialogContent>
               <AlertDialogHeader>
                  <AlertDialogTitle>{_js('Create a new category?')}</AlertDialogTitle>
                  <AlertDialogDescription>
                     {_js(
                        'The category "%1" does not exist yet. Saving creates it and moves the guide there.',
                        confirmCategory ?? ''
                     )}
                  </AlertDialogDescription>
               </AlertDialogHeader>
               <AlertDialogFooter>
                  <AlertDialogCancel>{_js('Cancel')}</AlertDialogCancel>
                  <AlertDialogAction
                     disabled={isDeleting}
                     onClick={() => void handleConfirmedCategorySave()}
                  >
                     {_js('Save and create')}
                  </AlertDialogAction>
               </AlertDialogFooter>
            </AlertDialogContent>
         </AlertDialog>

         {/* forceMount keeps tab state (form fields, selected step) alive
             across switches. */}
         <Tabs value={activeTab} onValueChange={setActiveTab}>
            {/* Below lg the title gets its own row with the tab strip under
                it (scrolling sideways when narrow); from lg up the strip
                shares the title's line to keep the content higher on the
                screen. The visibility chip lives on the title row, never
                inside the strip, so the sideways scroll can't hide it. */}
            <div className="pt-4 max-md:px-4 lg:flex lg:items-center lg:gap-6">
               <div className="flex min-w-0 items-center gap-2.5 lg:flex-1">
                  <h1
                     className="min-w-0 truncate text-lg font-semibold text-gray-950"
                     title={props.title}
                  >
                     {props.title}
                  </h1>
                  <VisibilityBadge
                     isPublic={isPublic}
                     canChangePrivacy={props.metadata.canChangePrivacy}
                     disabled={publishBusy}
                     onSetPublic={nextPublic => void handleSetPublic(nextPublic)}
                  />
               </div>
               <TabsList className="mt-3 lg:mt-0 lg:w-auto lg:shrink-0">
                  <EditorTab value="intro" icon="file-lines" dirty={tabDirty.intro}>
                     {_js('Guide')}
                  </EditorTab>
                  <EditorTab value="details" icon="screwdriver-wrench" dirty={tabDirty.details}>
                     {_js('Details')}
                  </EditorTab>
                  <EditorTab value="steps" icon="list-ol" dirty={tabDirty.steps}>
                     {_js('Steps')}
                  </EditorTab>
                  {hasMetadataTab && (
                     <EditorTab value="metadata" icon="memo-circle-info" dirty={tabDirty.metadata}>
                        {_js('Metadata')}
                     </EditorTab>
                  )}
               </TabsList>
            </div>

            <TabsContent
               value="intro"
               forceMount
               className="mt-2 data-[state=inactive]:hidden md:mt-4"
            >
               <SectionCard className="[&_input]:max-w-none [&_button]:max-w-none">
                  <IntroTab
                     fields={fields}
                     onChange={setFields}
                     introEditor={introEditor}
                     guideType={props.guideType}
                     isVideoGuide={isVideoGuide}
                     canSetVideoGuideMode={props.canSetVideoGuideMode}
                     titleAutogen={props.titleAutogen}
                     onImageProcessingChange={setIsImageProcessing}
                  />
                  {activeTab === 'intro' && (
                     <SaveBar
                        isSaving={isSaving}
                        disabled={saveDisabled}
                        onSave={() => void handleSave()}
                     />
                  )}
               </SectionCard>
            </TabsContent>

            <TabsContent
               value="details"
               forceMount
               className="mt-2 data-[state=inactive]:hidden md:mt-4"
            >
               <SectionCard className="[&_input]:max-w-none [&_button]:max-w-none">
                  <DetailsTab
                     guide={guide}
                     langid={props.langid}
                     bootPrerequisites={prerequisiteDetails}
                     timeIncrements={props.timeIncrements}
                     difficulties={props.difficulties}
                     flags={props.metadata.flags}
                     conclusionEditor={conclusionEditor}
                     fields={fields}
                     onChange={setFields}
                     canDelete={props.canDelete}
                     isDeleting={isDeleting}
                     isDeleteDisabled={isSaving || publish.isPending}
                     onDelete={() => void handleDelete()}
                  />
                  {activeTab === 'details' && (
                     <SaveBar
                        isSaving={isSaving}
                        disabled={saveDisabled}
                        onSave={() => void handleSave()}
                     />
                  )}
               </SectionCard>
            </TabsContent>

            <TabsContent
               value="steps"
               forceMount
               className="mt-2 data-[state=inactive]:hidden md:mt-4"
            >
               <StepsTab
                  guideid={props.guideid}
                  guide={guide}
                  initialStepid={props.initialStepid}
                  initialNewStepOrderby={props.initialNewStepOrderby}
                  // The selected field is authoritative: clearing it removes
                  // timestamps immediately, and replacing a hosted upload
                  // waits for that upload's own encodings instead of retaining
                  // the bootstrapped player's stale URLs.
                  video={stepVideo}
                  onDirtyChange={setStepsDirty}
                  onOpenShortcuts={() => setShortcutsOpen(true)}
               />
            </TabsContent>

            {hasMetadataTab && (
               <TabsContent
                  value="metadata"
                  forceMount
                  className="mt-2 data-[state=inactive]:hidden md:mt-4"
               >
                  <SectionCard className="[&_input]:max-w-none [&_button]:max-w-none">
                     <MetadataTab
                        guideid={props.guideid}
                        metadata={props.metadata}
                        initialAuthor={baseline.author}
                        fields={fields}
                        onChange={setFields}
                     />
                     {activeTab === 'metadata' && (
                        <SaveBar
                           isSaving={isSaving}
                           disabled={saveDisabled}
                           onSave={() => void handleSave()}
                        />
                     )}
                  </SectionCard>
               </TabsContent>
            )}
         </Tabs>

         <EditorFooter
            editorVersionLink={props.editorVersionLink}
            showLicenseDisclaimer={props.showLicenseDisclaimer}
            className="max-md:px-4"
         >
            <button
               type="button"
               onClick={() => setShortcutsOpen(true)}
               aria-label={_js('Keyboard shortcuts')}
               // Physical keyboards are a desktop affordance; hide the shortcuts
               // entry on phones and tablets, where it's noise.
               className="hidden text-gray-400 hover:text-gray-600 lg:block"
            >
               <FaIcon name="keyboard" className="size-4" />
            </button>
         </EditorFooter>

         <KeyboardShortcutsDialog open={shortcutsOpen} onOpenChange={setShortcutsOpen} />
      </>
   );
}

/**
 * The guide-level Save bar, the last child of each form tab's card: it
 * sticks to the viewport bottom while the card scrolls (steps save
 * themselves, so the Steps tab has none) and settles into the card's bottom
 * edge.
 */
function SaveBar({
   isSaving,
   disabled,
   onSave,
}: {
   isSaving: boolean;
   disabled: boolean;
   onSave: () => void;
}) {
   return (
      <div className={BAR_CLASSES}>
         <div className="flex items-center justify-end gap-3">
            <PrimaryButton isPending={isSaving} disabled={disabled} onClick={onSave}>
               {isSaving ? _js('Saving...') : _js('Save')}
            </PrimaryButton>
         </div>
      </div>
   );
}

const VISIBILITY_BADGE_CLASS =
   'flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium';

/**
 * The guide's visibility chip, pinned beside the title so the state reads
 * from every tab. Private is the loud, draft-like state (publishing is the
 * main thing left to do); public is quieter gray, dark enough to still read
 * as a control. With the change-privacy privilege the chip opens a one-item
 * menu for the opposite direction — Publish or Make private. Students (no
 * privilege) get the state read-only.
 */
function VisibilityBadge({
   isPublic,
   canChangePrivacy,
   disabled,
   onSetPublic,
}: {
   isPublic: boolean;
   canChangePrivacy: boolean;
   disabled: boolean;
   onSetPublic: (isPublic: boolean) => void;
}) {
   const stateChip = (
      <>
         <FaIcon name={isPublic ? 'globe' : 'lock'} className="size-3" aria-hidden />
         {isPublic ? _js('Public') : _js('Private')}
      </>
   );
   const stateColor = isPublic ? 'bg-gray-200 text-gray-700' : 'bg-brand-50 text-brand-700';
   if (!canChangePrivacy) {
      return <span className={cn(VISIBILITY_BADGE_CLASS, stateColor)}>{stateChip}</span>;
   }
   return (
      <DropdownMenu>
         <DropdownMenuTrigger asChild>
            <button
               type="button"
               disabled={disabled}
               className={cn(
                  VISIBILITY_BADGE_CLASS,
                  stateColor,
                  isPublic ? 'hover:bg-gray-300' : 'hover:bg-brand-100',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50'
               )}
            >
               {stateChip}
               <FaIcon name="chevron-down" className="size-2.5" aria-hidden />
            </button>
         </DropdownMenuTrigger>
         <DropdownMenuContent align="start">
            {isPublic ? (
               <DropdownMenuItem onSelect={() => onSetPublic(false)}>
                  <FaIcon name="lock" aria-hidden />
                  {_js('Make private')}
               </DropdownMenuItem>
            ) : (
               <DropdownMenuItem onSelect={() => onSetPublic(true)}>
                  <FaIcon name="globe" aria-hidden />
                  {_js('Publish')}
               </DropdownMenuItem>
            )}
         </DropdownMenuContent>
      </DropdownMenu>
   );
}

/**
 * ?media-manager deep-links straight into the Media Manager: open it on
 * load, and drop the param once it closes so a plain refresh doesn't
 * reopen it. There's no selection target on this path -- the dialog is
 * for browsing and managing the library, so the picked item is discarded.
 */
function useMediaManagerDeepLink(): void {
   useEffect(() => {
      if (!new URLSearchParams(window.location.search).has('media-manager')) {
         return;
      }
      void (async () => {
         const { openMediaManager } = await import('Shared/MediaManager');
         await openMediaManager({ accepts: 'guide_step_image' });
         const url = new URL(window.location.href);
         url.searchParams.delete('media-manager');
         window.history.replaceState(null, '', url);
      })();
   }, []);
}

/**
 * The loading placeholder. It mirrors the real editor's above-the-fold
 * shape — the tab bar plus the tab that will actually mount — so the layout
 * doesn't jump when the data arrives. The landing tab is read from the hash
 * (falling back to the server-parsed tab), the same precedence useHashTab
 * applies once mounted.
 */
function PageSkeleton({ initialTab }: { initialTab: string }) {
   const tab = window.location.hash.slice(1).split('/')[0] || initialTab;
   return (
      <div>
         {/* Tab bar: four pills roughly the width of the real tab labels,
             inside a padded strip the height of the real tab list. */}
         <div className="pt-4 max-md:px-4">
            <div className="inline-flex gap-1 rounded-lg bg-gray-100 p-1">
               <Skeleton className="h-7 w-14 rounded-md" />
               <Skeleton className="h-7 w-16 rounded-md" />
               <Skeleton className="h-7 w-14 rounded-md" />
               <Skeleton className="h-7 w-20 rounded-md" />
            </div>
         </div>
         <div className="mt-2 md:mt-4">
            {tab === 'steps' ? <StepsSkeleton /> : <FormSkeleton />}
         </div>
      </div>
   );
}

/** A labelled field: caption, control, and (usually) a hint line below. */
function FieldSkeleton({ hint = true }: { hint?: boolean }) {
   return (
      <div className="flex flex-col gap-2">
         <Skeleton className="h-4 w-24" />
         <Skeleton className="h-9 w-full rounded-md" />
         {hint && <Skeleton className="h-3 w-48" />}
      </div>
   );
}

/**
 * The Introduction/Guide tab (the default landing): a form column beside the
 * guide image, with the introduction editor below. Details and Metadata land
 * on this shape too — close enough that the card doesn't resize under them.
 */
function FormSkeleton() {
   return (
      <SectionCard>
         <div className="flex flex-col gap-6">
            <FieldSkeleton />
            <FieldSkeleton />
            <FieldSkeleton />
            <FieldSkeleton hint={false} />
            <div>
               <Skeleton className="mb-1 h-4 w-24" />
               <Skeleton className="h-48 w-full rounded-lg" />
            </div>
            <Skeleton className="h-48 w-full rounded-lg" />
         </div>
      </SectionCard>
   );
}

/** The Steps tab: the step rail beside the step editor. */
function StepsSkeleton() {
   return (
      <div className="grid grid-cols-1 gap-4 md:grid-cols-[16rem_1fr]">
         <div className="hidden flex-col gap-2 md:flex">
            {[0, 1, 2, 3, 4, 5].map(index => (
               <Skeleton key={index} className="h-12 rounded-lg" />
            ))}
         </div>
         <Skeleton className="h-[460px] rounded-lg" />
      </div>
   );
}

function mergeUntouchedFields(
   current: GuideEditFields,
   baseline: GuideEditFields,
   fresh: GuideEditFields
): GuideEditFields {
   const merged: GuideEditFields = { ...fresh };
   for (const key of Object.keys(fresh) as (keyof GuideEditFields)[]) {
      if (JSON.stringify(current[key]) !== JSON.stringify(baseline[key])) {
         // The user touched it; their in-progress value wins.
         (merged as Record<string, unknown>)[key] = current[key];
      }
   }
   return merged;
}

/** A stale-revisionid rejection: the guide changed since it was loaded. */
function isConflictError(error: Error): boolean {
   return ApiClient.isApiError(error) && error.response?.status === 409;
}

function errorMessage(error: Error): string {
   return apiErrorMessage(error, {
      login: _js('Log in to edit this guide.'),
      conflict: _js(
         'The guide changed since you loaded this page. Reload to pick up the latest version, then redo your changes.'
      ),
   });
}

function deleteErrorMessage(error: Error): string {
   return apiErrorMessage(error, {
      login: _js('Log in to delete this guide.'),
      conflict: _js(
         'The guide changed since you loaded this page. Reload the page, then try deleting again.'
      ),
   });
}
